import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Save } from "lucide-react";
import type { Agent } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { useSetAgentBudget, useSetAgentTurnBudget } from "@/api/mutations/budget";
import { useAgentBudget } from "@/api/queries/budget";

// #223 UC-5: Budget-Sektion auf der Agent-Detail-Seite. Zeigt den aktuellen
// Monatsstand (Agent + User-Summe) + Blocked-Status und lässt den Admin das
// Monats-Limit setzen (leer = Plattform-Default greift).
function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

export function BudgetSection({ agent }: { agent: Agent }) {
  const setBudget = useSetAgentBudget(agent.id);
  const setTurnBudget = useSetAgentTurnBudget(agent.id);
  const statusQuery = useAgentBudget(agent.id);
  const status = statusQuery.data;

  // #223 UC-A: Per-Turn-Cap (getrennter State/Save vom Monats-Limit).
  const turnInitial = agent.turnBudget?.limitUsd != null ? String(agent.turnBudget.limitUsd) : "";
  const [turnLimit, setTurnLimit] = useState<string>(turnInitial);
  const [turnDirty, setTurnDirty] = useState(false);
  useEffect(() => {
    if (!turnDirty) setTurnLimit(agent.turnBudget?.limitUsd != null ? String(agent.turnBudget.limitUsd) : "");
  }, [agent.turnBudget, turnDirty]);

  async function saveTurn() {
    const trimmed = turnLimit.trim();
    let value: number | null;
    if (trimmed === "") {
      value = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        toast.error("Per-Turn-Cap muss eine Zahl >= 0 sein (0 = unbegrenzt, leer = Plattform-Default)");
        return;
      }
      value = n;
    }
    try {
      await setTurnBudget.mutateAsync(value);
      setTurnDirty(false);
      toast.success("Per-Turn-Cap gespeichert");
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  const initial =
    agent.budgetPolicy?.monthlyLimitUsd != null ? String(agent.budgetPolicy.monthlyLimitUsd) : "";
  const [limit, setLimit] = useState<string>(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) {
      setLimit(agent.budgetPolicy?.monthlyLimitUsd != null ? String(agent.budgetPolicy.monthlyLimitUsd) : "");
    }
  }, [agent.budgetPolicy, dirty]);

  async function save() {
    const trimmed = limit.trim();
    let value: number | null;
    if (trimmed === "") {
      value = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        toast.error("Limit muss eine Zahl >= 0 sein (oder leer für Plattform-Default)");
        return;
      }
      value = n;
    }
    try {
      await setBudget.mutateAsync(value);
      setDirty(false);
      toast.success("Budget-Limit gespeichert");
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <SectionCard
      icon={<Wallet className="h-[18px] w-[18px]" />}
      title="Budget"
      description="Monatliches Kosten-Limit (USD) + aktueller Verbrauch"
    >
      <div className="space-y-4">
        {status && (
          <div className="grid grid-cols-2 gap-3">
            <StatTile
              label={`Agent (${status.yearMonth})`}
              value={`${fmtUsd(status.agentMtdUsd)} / ${fmtUsd(status.agentLimitUsd)}`}
              alert={status.agentMtdUsd >= status.agentLimitUsd}
            />
            {status.userMtdUsd != null && status.userLimitUsd != null && (
              <StatTile
                label="User-Summe"
                value={`${fmtUsd(status.userMtdUsd)} / ${fmtUsd(status.userLimitUsd)}`}
                alert={status.userMtdUsd >= status.userLimitUsd}
              />
            )}
          </div>
        )}

        {status?.blocked && (
          <div
            className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-[12.5px] text-danger"
            data-testid="budget-blocked"
          >
            Dieser Agent ist aktuell durch das Budget blockiert — pausiert bis Monatswechsel oder Limit-Erhöhung.
          </div>
        )}

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-text2">Monats-Limit (USD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={limit}
            onChange={(e) => {
              setLimit(e.target.value);
              setDirty(true);
            }}
            placeholder="leer = Plattform-Default"
            className="flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50"
            disabled={setBudget.isPending}
            data-testid="budget-limit-input"
          />
          <span className="mt-1 block text-[11px] text-text3">
            Leer ⇒ Plattform-Default greift. Erreicht ⇒ der Agent pausiert bis zum Monatswechsel oder bis du das Limit anhebst.
          </span>
        </div>

        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={!dirty || setBudget.isPending} data-testid="budget-save">
            <Save className="h-4 w-4" />
            Speichern
          </Button>
        </div>

        {/* #223 UC-A: Per-Turn-Budget-Cap — Runaway-Schutz pro Turn, live ab nächstem Turn. */}
        <div className="border-t border-border pt-4">
          <label className="mb-1 block text-[12px] font-semibold text-text2">Per-Turn-Cap (USD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={turnLimit}
            onChange={(e) => {
              setTurnLimit(e.target.value);
              setTurnDirty(true);
            }}
            placeholder="leer = Plattform-Default"
            className="flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50"
            disabled={setTurnBudget.isPending}
            data-testid="turn-budget-input"
          />
          <span className="mt-1 block text-[11px] text-text3">
            Bricht einen einzelnen Turn ab, wenn seine geschätzten Kosten den Wert überschreiten (Runaway-Schutz).
            Leer ⇒ Plattform-Default. <b>0 ⇒ unbegrenzt</b> (kein Cap). Greift live ab dem nächsten Turn.
          </span>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={saveTurn} disabled={!turnDirty || setTurnBudget.isPending} data-testid="turn-budget-save">
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function StatTile({ label, value, alert }: { label: string; value: string; alert: boolean }) {
  return (
    <div className="rounded-md border border-input bg-base px-3 py-2">
      <div className="text-[11px] text-text3">{label}</div>
      <div className={`mt-0.5 font-mono text-[13px] ${alert ? "font-semibold text-danger" : "text-text"}`}>
        {value}
      </div>
    </div>
  );
}
